import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import PasswordInput from '../components/ui/password-input';
import PasswordStrengthMeter from '../components/ui/password-strength-meter';
import { Flower, CheckCircle, WarningTriangle, Refresh } from 'iconoir-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_ATTEMPTS = 12;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();

  // status: polling | needs_signup | existing_user | paid | expired | timeout | error
  const [status, setStatus] = useState('polling');
  const [email, setEmail] = useState('');
  const [signupToken, setSignupToken] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const attemptsRef = useRef(0);
  const pollingRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    if (pollingRef.current) return;
    pollingRef.current = true;

    const poll = async () => {
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setStatus('timeout');
        return;
      }
      attemptsRef.current += 1;
      try {
        // Use public endpoint so anonymous flow works (authenticated still works too)
        const { data } = await axios.get(`${API}/payments/status-public/${sessionId}`, { withCredentials: true });
        if (data.payment_status === 'paid') {
          if (data.signup_token) {
            setSignupToken(data.signup_token);
            setEmail(data.email || '');
            setStatus('needs_signup');
          } else if (data.existing_user) {
            // Existing user — if they're already logged in, auto-redirect; else ask them to sign in
            setEmail(data.email || '');
            await checkAuth();
            setStatus('existing_user');
          } else {
            setStatus('paid');
          }
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus('error');
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();
  }, [sessionId, checkAuth]);

  const handleCompleteSignup = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/complete-signup`,
        { signup_token: signupToken, name: name.trim(), password },
        { withCredentials: true }
      );
      await checkAuth();
      toast.success('Welcome to Shaadi Planner!');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Signup failed');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-page flex items-center justify-center px-6" data-testid="payment-success-page">
      <div className="card-surface p-10 max-w-md w-full">
        <div className="flex justify-center mb-6">
          <Flower className="w-10 h-10 text-terracotta" strokeWidth={1.5} />
        </div>

        {status === 'polling' && (
          <div className="text-center">
            <Refresh className="w-10 h-10 text-muted mx-auto mb-4 animate-spin" strokeWidth={1.5} />
            <h1 className="font-serif text-3xl text-charcoal mb-3" data-testid="payment-status-polling">
              Confirming your payment…
            </h1>
            <p className="text-muted text-sm">
              Hang tight — we're verifying with Stripe. This usually takes a few seconds.
            </p>
          </div>
        )}

        {status === 'needs_signup' && (
          <div>
            <div className="text-center mb-8">
              <CheckCircle className="w-12 h-12 text-moss mx-auto mb-4" strokeWidth={1.5} />
              <h1 className="font-serif text-3xl text-charcoal mb-2" data-testid="payment-status-needs-signup">
                Payment received — welcome!
              </h1>
              <p className="text-muted text-sm">
                One last step: create your account. Your email is already set.
              </p>
            </div>
            <form onSubmit={handleCompleteSignup} className="space-y-4" data-testid="complete-signup-form">
              <div>
                <label className="block text-xs tracking-widest uppercase font-mono text-muted mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  disabled
                  className="w-full border border-border px-4 py-3 bg-page-alt text-muted font-sans"
                  data-testid="complete-signup-email"
                />
              </div>
              <div>
                <label className="block text-xs tracking-widest uppercase font-mono text-muted mb-2">Your name</label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                  className="w-full border border-border px-4 py-3 bg-white font-sans focus:outline-none focus:border-terracotta transition-colors"
                  data-testid="complete-signup-name"
                />
              </div>
              <div>
                <label className="block text-xs tracking-widest uppercase font-mono text-muted mb-2">Password (min 8 chars)</label>
                <PasswordInput
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-border px-4 py-3 bg-white font-sans focus:outline-none focus:border-terracotta transition-colors h-auto rounded-none shadow-none"
                  data-testid="complete-signup-password"
                />
                <PasswordStrengthMeter password={password} data-testid="complete-signup-password-strength" />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full py-6"
                data-testid="complete-signup-submit"
              >
                {submitting ? 'Creating your account…' : 'Create Account & Open Dashboard'}
              </Button>
            </form>
          </div>
        )}

        {status === 'existing_user' && (
          <div className="text-center">
            <CheckCircle className="w-14 h-14 text-moss mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-serif text-3xl text-charcoal mb-3" data-testid="payment-status-existing">
              Payment confirmed!
            </h1>
            <p className="text-muted text-sm mb-6">
              Your account with <span className="font-medium text-charcoal">{email}</span> has been upgraded.
              {user?.is_paid ? " Redirecting to dashboard…" : " Please sign in to continue."}
            </p>
            <Button
              onClick={() => navigate(user?.is_paid ? '/dashboard' : '/login', { replace: true })}
              className="btn-primary w-full"
              data-testid="existing-user-continue-btn"
            >
              {user?.is_paid ? 'Go to Dashboard' : 'Sign In'}
            </Button>
          </div>
        )}

        {status === 'paid' && (
          <div className="text-center">
            <CheckCircle className="w-14 h-14 text-moss mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-serif text-3xl text-charcoal mb-3" data-testid="payment-status-paid">
              Welcome to Shaadi Planner!
            </h1>
            <p className="text-muted text-sm mb-6">Payment successful.</p>
            <Button onClick={() => navigate('/dashboard', { replace: true })} className="btn-primary w-full" data-testid="go-to-dashboard-btn">
              Go to Dashboard
            </Button>
          </div>
        )}

        {(status === 'expired' || status === 'error' || status === 'timeout') && (
          <div className="text-center">
            <WarningTriangle className="w-12 h-12 text-terracotta mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-serif text-3xl text-charcoal mb-3" data-testid="payment-status-error">
              {status === 'expired' ? 'Checkout expired' : status === 'timeout' ? 'Still processing…' : 'Could not confirm payment'}
            </h1>
            <p className="text-muted text-sm mb-6">
              {status === 'expired'
                ? 'Your checkout session expired. Please try again from the homepage.'
                : status === 'timeout'
                ? "We couldn't confirm your payment yet. If you were charged, refresh this page in a moment — your access will unlock automatically."
                : "Something went wrong while confirming your payment. If you were charged, please contact support."}
            </p>
            <Button onClick={() => navigate('/', { replace: true })} className="btn-primary w-full" data-testid="back-home-btn">
              Back to Homepage
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
